import { memo, useCallback, useEffect, useState } from "react";

import { classNames } from "shared/lib/classNames/classNames";

import cls from "./SociometryResultsPage.module.scss";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ISociometrySessionResult } from "shared/types/sociometry";
import { SociometryMutualPairsList, SociometryResultsTable } from "components/sociometry";
import { Layout } from "components/Layout";
import { BackButton } from "components/BackButton";
import { VStack } from "components/landingNew/shared/ui/Stack";
import { getSociometrySessionStatusMeta } from "pages/sociometry/helpers/sociometrySessionStatusMap";
import { useCreateReport } from "hooks/useCreateReport";

interface SociometryResultsPageProps {
    className?: string;
}

export const SociometryResultsPage = memo((props: SociometryResultsPageProps) => {
    const { className } = props;
    const { id: sessionId } = useParams<{ id: string }>();
    const [sessionResult, setSessionResult] = useState<ISociometrySessionResult | null>(null);
    const { createReport, isReportLoading } = useCreateReport();

    const loadResult = useCallback(async () => {
        try {
            const { data } = await axios.get(`sociometry/psych/session/${sessionId}/result`);
            setSessionResult(data);
        } catch (e) {
            console.error(e);
        }
    }, [sessionId]);

    useEffect(() => {
        loadResult();
    }, [loadResult]);

    const statusLabel = getSociometrySessionStatusMeta(sessionResult?.status || "Pending").label;
    const statusColor = getSociometrySessionStatusMeta(sessionResult?.status || "Pending").color;

    const hasResults = !!sessionResult && sessionResult.scores.length > 0;
    const classLabel = sessionResult
        ? `${sessionResult.class.number}${sessionResult.class.letter ? "-" + sessionResult.class.letter : ""}`
        : "";

    const handleDownloadExcel = () => {
        if (!sessionId) return;
        createReport(`sociometry/psych/session/${sessionId}/excel`, `Социометрия_${classLabel}.xlsx`);
    };

    if (sessionResult?.scores.length === 0) {
        return (
            <Layout>
                <div className={classNames(cls.sociometryResultsPage, {}, [className])}>
                    <header className={cls.header}>
                        <BackButton />
                        <p className={cls.title}>Социометрия</p>
                    </header>
                    <div className={cls.classUnit}>
                        <p>
                            {sessionResult?.class.number}
                            {sessionResult?.class?.letter}
                        </p>
                    </div>
                    <div className={cls.content}>
                        <VStack align="center" max gap={32}>
                            <VStack align="center" max gap={8}>
                                <div className={cls.status} style={{ backgroundColor: statusColor }}>
                                    {statusLabel}
                                </div>
                                <p
                                    className={cls.passedTest}
                                >{`Прошли тест ${sessionResult?.totalRespondents} из ${sessionResult?.totalPupilsInClass} учеников`}</p>
                            </VStack>
                            <h3 className={cls.sectionTitle}>Нет данных для отображения</h3>
                        </VStack>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={classNames(cls.sociometryResultsPage, {}, [className])}>
                <header className={cls.header}>
                    <BackButton />
                    <p className={cls.title}>Социометрия</p>
                </header>
                <div className={cls.classUnit}>
                    <p>
                        {sessionResult?.class.number}
                        {sessionResult?.class?.letter}
                    </p>
                </div>
                <div className={cls.content}>
                    <VStack align="center" max gap={32}>
                        <VStack align="center" max gap={8}>
                            <div className={cls.statusRow}>
                                {hasResults && (
                                    <button
                                        type="button"
                                        className={cls.downloadBtn}
                                        onClick={handleDownloadExcel}
                                        disabled={isReportLoading}
                                    >
                                        {isReportLoading ? "Загрузка..." : "Скачать Excel"}
                                    </button>
                                )}
                                <div className={cls.status} style={{ backgroundColor: statusColor }}>
                                    {statusLabel}
                                </div>
                            </div>
                            <p
                                className={cls.passedTest}
                            >{`Прошли тест ${sessionResult?.totalRespondents} из ${sessionResult?.totalPupilsInClass} учеников`}</p>
                        </VStack>
                        <VStack align="center" max gap={24}>
                            <h3 className={cls.sectionTitle}>Результаты сессии</h3>
                            <SociometryResultsTable
                                scores={sessionResult?.scores || []}
                                sessionId={sessionId}
                                onAnnulled={loadResult}
                            />
                        </VStack>
                    </VStack>

                    <VStack className={cls.mutualPairsSection} gap={24} align="center">
                        <h3 className={cls.sectionTitle}>Взаимные пары</h3>
                        <SociometryMutualPairsList pairs={sessionResult?.mutualPairs || []} />
                    </VStack>
                </div>
            </div>
        </Layout>
    );
});

SociometryResultsPage.displayName = "SociometryResultsPage";
